import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PROPERTY_FAQ_CATEGORIES } from "@/content/property-corpus/faq-defs";

export const Route = createFileRoute("/en/property/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/property-corpus");
    return { faqs: m.PROPERTY_FAQ_EN };
  },
  component: PropertyFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Foreign Property Ownership in Thailand — FAQ (${faqs.length})`,
        description:
          "Foreign buyer FAQ: the 49% condominium quota and FET evidence, land and house restrictions, 30-year leases, usufruct and superficies, title-deed due diligence, Land Department transfer fees and taxes, powers of attorney (Tor Dor 21 / Or Chor 21), nominee risk, spouse and inheritance rules.",
        path: "/en/property/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/property/faq" }],
        keywords: [
          "foreigner buy condo thailand",
          "49% foreign quota condominium",
          "thailand land ownership foreigner",
          "usufruct thailand",
          "land department transfer fee thailand",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Property Ownership FAQ", url: `${site}/en/property/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/property/faq")) },
      ],
    };
  },
});

function PropertyFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Property Ownership FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Property Purchase, Leasing and Title Transfer for Foreigners — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow the Condominium Act B.E. 2522 (as amended), the Land Code, the Civil and Commercial Code,
          the Land Department fee schedule (dol.go.th), Revenue Department rules (rd.go.th) and Bank of Thailand
          notifications on inbound foreign currency (bot.or.th). This is general information, not case-specific
          legal advice, and no Land Department outcome is guaranteed.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/property/faq" className="text-primary underline">FAQ ซื้ออสังหาฯ และโอนกรรมสิทธิ์ →</a>
        </div>
        <section aria-label="Property FAQ topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {PROPERTY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/property/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial services and powers of attorney</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation</a></li>
            <li><a className="text-primary underline" href="/en/consular-legalization/faq">MFA legalisation and Apostille</a></li>
            <li><a className="text-primary underline" href="/en/marriage/faq">Marriage registration</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Still need an answer?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask our team about document lists, Land Department sequencing and timing by phone, LINE or email — Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact the team →</a>
        </section>
        <FaqSourceNote topic="property" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
